import React from 'react';
import { ENV_CONFIG } from '../constants';
import { 
  ClockIcon, 
  EnvelopeIcon,
  GlobeAltIcon,
  LockClosedIcon,
  UserGroupIcon 
} from '@heroicons/react/24/outline';

const ComingSoonPage: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(/cseangroup.jpeg)',
          filter: 'blur(1px)',
        }}
      >
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-black/65"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          {/* Logo Section */}
          <div className="mb-10">
            <div className="inline-flex items-center justify-center w-48 h-36 bg-white/95 rounded-lg mb-6 shadow-lg p-4">
              <img 
                src="/cseans-logo.png" 
                alt="CSEAN Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-4 font-display">
              {ENV_CONFIG.siteName}
            </h1>
            <p className="text-xl md:text-2xl text-primary-100 font-light">
              {ENV_CONFIG.siteTagline}
            </p>
          </div>

          {/* Coming Soon Message */}
          <div className="mb-16">
            <div className="inline-flex items-center bg-black/40 rounded-full px-6 py-3 mb-8 border border-white/30">
              <ClockIcon className="w-5 h-5 text-white mr-3" />
              <span className="text-white font-medium">Coming Soon</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Something Amazing is Coming
            </h2>
            
            <p className="text-lg md:text-xl text-primary-100 mb-8 max-w-2xl mx-auto leading-relaxed">
              We're working hard to bring you a comprehensive platform for cybersecurity professionals. 
              Our new website will feature enhanced membership services, professional resources, and 
              cutting-edge security insights.
            </p>
          </div>

          {/* Features Preview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
            <div className="bg-black/35 hover:bg-black/45 transition-colors rounded-lg p-6 border border-white/10">
              <UserGroupIcon className="w-12 h-12 text-primary-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Professional Network</h3>
              <p className="text-primary-100">
                Connect with cybersecurity experts and industry leaders worldwide
              </p>
            </div>
            
            <div className="bg-black/35 hover:bg-black/45 transition-colors rounded-lg p-6 border border-white/10">
              <LockClosedIcon className="w-12 h-12 text-primary-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Security Resources</h3>
              <p className="text-primary-100">
                Access the latest research, reports, and best practices
              </p>
            </div>
            
            <div className="bg-black/35 hover:bg-black/45 transition-colors rounded-lg p-6 border border-white/10">
              <GlobeAltIcon className="w-12 h-12 text-primary-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Global Impact</h3>
              <p className="text-primary-100">
                Join a community driving cybersecurity excellence in Nigeria
              </p>
            </div>
          </div>

          {/* Available Now Section */}
          <div className="bg-black/35 rounded-xl p-8 border border-white/10">
            <h3 className="text-2xl font-bold text-white mb-4">
              Available Now
            </h3>
            <p className="text-primary-100 mb-6">
              While we prepare our full platform, you can already explore our membership options:
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/membership/categories"
                className="btn-primary inline-flex items-center justify-center px-8 py-3 text-lg font-semibold"
              >
                View Membership Categories
              </a>
              <a
                href="/membership/application"
                className="btn-secondary inline-flex items-center justify-center px-8 py-3 text-lg font-semibold"
              >
                Start Application
              </a>
            </div>
          </div>

          {/* Contact Information */}
          <div className="mt-16 text-center">
            <p className="text-primary-200 mb-4">
              Questions? We'd love to hear from you
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-primary-100">
              <a
                href="mailto:info@csean.org.ng"
                className="inline-flex items-center hover:text-white transition-colors"
              >
                <EnvelopeIcon className="w-5 h-5 mr-2" />
                info@csean.org.ng
              </a>
              <span className="hidden sm:block">•</span>
              <a
                href="https://csean.org.ng"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center hover:text-white transition-colors"
              >
                <GlobeAltIcon className="w-5 h-5 mr-2" />
                csean.org.ng
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0 p-6 text-center">
        <p className="text-primary-200 text-sm">
          © {currentYear} {ENV_CONFIG.siteName}. All Rights Reserved.
        </p>
      </div>
    </div>
  );
};

export default ComingSoonPage;
